import { isLeft } from "fp-ts/Either";

import {
  TSensitiveCriteria,
  ShapeSensitiveCriteria,
} from "../survey/SurveyContainer";

/**
 * Checks if local storage is available.
 * From Mozilla: https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#testing_for_availability
 * @returns True if available, false or undefined if not
 */
function localStorageAvailable(): boolean | undefined {
  let storage;
  try {
    storage = window["localStorage"];
    const x = "__storage_test__";
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === "QuotaExceededError" ||
        // Firefox
        e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
}

const SENSITIVE_DATA_LOCAL_STORAGE_KEY = "sensitiveCriteria";

/**
 * Retrieve stored sensitive data if it exists.
 * Silently fails since if we fail to retrieve sensitive criteria it doesn't block the user.
 * @returns The stored sensitive data if stored. Null if not stored.
 */
export function getSensitiveCriteria(): TSensitiveCriteria | null {
  // Check we can use local storage
  if (localStorageAvailable() !== true) {
    return null;
  }

  // Retrieve
  const rawStored = localStorage.getItem(SENSITIVE_DATA_LOCAL_STORAGE_KEY);
  if (rawStored === null) {
    // Not found
    return null;
  }

  // Decode
  let rawDeserialized = {};
  try {
    rawDeserialized = JSON.parse(rawStored);
  } catch (e) {
    console.error(
      "Failed to deserialize JSON sensitive criteria from local storage",
      e
    );
    return null;
  }

  const decoded = ShapeSensitiveCriteria.decode(rawDeserialized);

  if (isLeft(decoded)) {
    // Decode error
    return null;
  }

  return decoded.right;
}

/**
 * Save sensitive criteria in local storage.
 * Silently fails because not saving sensitive criteria is not blocking.
 * @param criteria The sensitive criteria to save
 */
export function saveSensitiveCriteria(criteria: TSensitiveCriteria) {
  // Check we can use local storage
  if (localStorageAvailable() !== true) {
    return;
  }

  // Serialize
  let rawSerialized = "";
  try {
    rawSerialized = JSON.stringify(criteria);
  } catch (e) {
    console.error("Failed to serialize sensitive criteria as JSON", e);
    return;
  }

  // Save
  localStorage.setItem(SENSITIVE_DATA_LOCAL_STORAGE_KEY, rawSerialized);
}

/**
 * Clear sensitive criteria from local storage.
 */
export function clearSensitiveCriteria() {
  // Check we can use local storage
  if (localStorageAvailable() !== true) {
    return;
  }

  localStorage.removeItem(SENSITIVE_DATA_LOCAL_STORAGE_KEY);
}
